import { useCallback, useState } from 'react'
import * as Sentry from '@sentry/react'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackTimelineEvent } from '@/analytics/utils/timeline-analytics'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { useAppDispatch } from '@/store/store-hooks'
import { type TimelineSourceRef } from './schemas'
import { openTimelineSourceViewer, type OpenTimelineSourceViewerOptions } from './open-timeline-source-viewer'

type SourceLoadError = {
  sourceId: string
  message: string
} | null

function buildTimelineOpenTelemetry(source: TimelineSourceRef, options: OpenTimelineSourceViewerOptions) {
  return {
    tags: {
      contentViewerFeature: 'timeline',
      sourceProvider: source.provider,
      referenceBacked: String(Boolean(options.referenceId)),
    },
    extra: {
      sourceUserFileId: source.source_id,
      timelineReferenceId: options.referenceId ?? null,
    },
  }
}

export function useOpenTimelineSource() {
  const dispatch = useAppDispatch()
  const [sourceLoadError, setSourceLoadError] = useState<SourceLoadError>(null)

  const openTimelineSource = useCallback(
    async (source: TimelineSourceRef, options: OpenTimelineSourceViewerOptions = {}) => {
      setSourceLoadError(null)

      try {
        const result = await openTimelineSourceViewer({
          source,
          options,
          dispatch,
        })

        if (!result.opened) {
          const telemetry = buildTimelineOpenTelemetry(source, options)
          Sentry.captureMessage('Timeline source viewer open failed', {
            level: 'warning',
            tags: telemetry.tags,
            extra: {
              ...telemetry.extra,
              errorMessage: result.error,
            },
          })
          trackTimelineEvent(AnalyticsEvent.TimelineSourceOpenFailed, { timelineSourceId: source.source_id })
          setSourceLoadError({ sourceId: source.source_id, message: result.error })
          return false
        }

        dispatch(ContentViewerActions.setContentViewerSession({ session: result.session }))
        return true
      } catch (error) {
        Sentry.captureException(error, buildTimelineOpenTelemetry(source, options))
        trackTimelineEvent(AnalyticsEvent.TimelineSourceOpenFailed, { timelineSourceId: source.source_id })
        setSourceLoadError({ sourceId: source.source_id, message: 'Could not load this source file.' })
        return false
      }
    },
    [dispatch]
  )

  return {
    openTimelineSource,
    sourceLoadError,
  }
}
